<template>
  <v-dialog v-model="dialog" max-width="900px" persistent>
    <v-card class="analise-ambiente-card">
      <!-- Header -->
      <v-card-title class="analise-header">
        <div class="analise-title">
          <v-icon left color="warning" size="24">mdi-alert-circle</v-icon>
          <span>Análise de Ambiente</span>
        </div>
        <v-btn icon small @click="fechar" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="analise-content pa-0">
        <!-- Loading State -->
        <div v-if="carregando" class="text-center py-12">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <p class="mt-4 analise-loading-text">Analisando ambiente...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="erro" class="text-center py-12">
          <v-icon color="error" size="64">mdi-alert-circle</v-icon>
          <p class="mt-4 analise-error-text">{{ erro }}</p>
          <v-btn color="primary" @click="analisar" class="mt-4">
            Tentar Novamente
          </v-btn>
        </div>

        <!-- Content -->
        <div v-else>
          <!-- Summary Box -->
          <div class="summary-box" :class="{ 'success': totalInconsistencias === 0 }">
            <v-icon :color="totalInconsistencias === 0 ? 'success' : 'warning'" size="24">
              {{ totalInconsistencias === 0 ? 'mdi-check-circle' : 'mdi-alert' }}
            </v-icon>
            <div class="summary-content">
              <h3>
                {{ totalInconsistencias === 0 ? 'Nenhuma inconsistência encontrada!' : `${totalInconsistencias} inconsistência(s) encontrada(s)` }}
              </h3>
              <p>Total de cronogramas analisados: {{ totalCronogramas }}</p>
            </div>
          </div>

          <!-- List Container -->
          <div v-if="totalInconsistencias > 0" class="list-container">
            <!-- List Header -->
            <div class="list-header">
              <span>Hostname</span>
              <span>Motivo</span>
            </div>

            <!-- List Rows -->
            <div 
              v-for="(item, index) in paginatedItems" 
              :key="index"
              class="list-row"
            >
              <div class="hostname-col">
                <v-icon left small class="server-icon">mdi-server</v-icon>
                <strong>{{ item.hostName }}</strong>
              </div>
              <div class="motivos-col">
                <v-tooltip
                  v-for="(motivo, motivoIndex) in item.motivos"
                  :key="motivoIndex"
                  bottom
                  :disabled="!getTooltipText(item, motivo)"
                  max-width="300"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      class="badge"
                      :class="getMotivoBadgeClass(motivo)"
                    >
                      <v-icon left x-small>{{ getMotivoIcon(motivo) }}</v-icon>
                      {{ motivo }}
                    </span>
                  </template>
                  <div v-if="getTooltipText(item, motivo)" class="tooltip-content">
                    <div v-for="(linha, linhaIndex) in getTooltipLines(item, motivo)" :key="linhaIndex">
                      {{ linha }}
                    </div>
                  </div>
                </v-tooltip>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Footer -->
      <v-card-actions class="analise-footer" v-if="!carregando && !erro && totalInconsistencias > 0">
        <div class="pagination-info">
          <span>Rows per page: <strong>{{ itemsPerPage }}</strong></span>
          <span class="ml-4">{{ paginationStart }}-{{ paginationEnd }} of {{ totalInconsistencias }}</span>
          <div class="pagination-controls ml-4">
            <v-btn 
              icon 
              x-small 
              @click="previousPage" 
              :disabled="currentPage === 1"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn 
              icon 
              x-small 
              @click="nextPage" 
              :disabled="currentPage === totalPages"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="footer-actions">
          <v-btn text @click="fechar" :disabled="desativando || ajustando" class="btn-ghost">
            Fechar
          </v-btn>
          <v-btn 
            color="warning" 
            @click="ajustarHorarios"
            :disabled="!temConflitosBackup || ajustando || desativando"
            :loading="ajustando"
            class="mr-2"
          >
            <v-icon left>mdi-clock-edit</v-icon>
            Ajustar Horários
          </v-btn>
          <v-btn 
            color="error" 
            @click="desativarCronogramas"
            :disabled="totalInconsistencias === 0 || desativando"
            :loading="desativando"
            class="btn-primary"
          >
            <v-icon left>mdi-pause-circle</v-icon>
            Desativar Cronogramas
          </v-btn>
        </div>
      </v-card-actions>

      <!-- Footer sem paginação (quando não há inconsistências) -->
      <v-card-actions class="analise-footer" v-if="!carregando && !erro && totalInconsistencias === 0">
        <v-spacer></v-spacer>
        <v-btn text @click="fechar" class="btn-ghost">
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import hostsService from '@/services/hostsService'

export default {
  name: 'AnalisarAmbiente',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      carregando: false,
      erro: null,
      totalCronogramas: 0,
      totalInconsistencias: 0,
      inconsistencias: [],
      desativando: false,
      ajustando: false,
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    totalPages() {
      return Math.ceil(this.totalInconsistencias / this.itemsPerPage)
    },
    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1
    },
    paginationEnd() {
      const end = this.currentPage * this.itemsPerPage
      return end > this.totalInconsistencias ? this.totalInconsistencias : end
    },
    paginatedItems() {
      if (!Array.isArray(this.inconsistencias) || this.inconsistencias.length === 0) {
        return []
      }
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.inconsistencias.slice(start, end)
    },
    temConflitosBackup() {
      return this.inconsistencias.some(inc => 
        inc.motivos && inc.motivos.includes('Horários em conflito com Backup')
      )
    }
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.analisar()
      } else {
        this.resetar()
      }
    }
  },
  methods: {
    async analisar() {
      this.carregando = true
      this.erro = null
      
      try {
        const response = await hostsService.analisarAmbiente()
        
        // O interceptor do axios já retorna response.data, então response já é o objeto de dados
        if (response && response.success) {
          this.totalCronogramas = response.totalCronogramas || 0
          this.totalInconsistencias = response.totalInconsistencias || 0
          this.inconsistencias = Array.isArray(response.inconsistencias) ? response.inconsistencias : []
          // Resetar página quando receber novos dados
          this.currentPage = 1
          
          // Emitir evento para o componente pai atualizar a notificação
          this.$emit('analise-concluida', {
            totalInconsistencias: this.totalInconsistencias,
            totalCronogramas: this.totalCronogramas
          })
          
          // Debug temporário para verificar dados
          console.log('[AnalisarAmbiente] Dados recebidos:', {
            totalCronogramas: this.totalCronogramas,
            totalInconsistencias: this.totalInconsistencias,
            inconsistenciasLength: this.inconsistencias.length,
            paginatedItemsLength: this.paginatedItems.length,
            primeiraInconsistencia: this.inconsistencias[0],
            response: response
          })
        } else {
          this.erro = response?.error || 'Erro ao analisar ambiente'
        }
      } catch (error) {
        console.error('[AnalisarAmbiente] Erro ao analisar ambiente:', error)
        this.erro = error.response?.data?.error || error.message || 'Erro ao analisar ambiente'
      } finally {
        this.carregando = false
      }
    },
    
    async desativarCronogramas() {
      if (this.inconsistencias.length === 0) {
        return
      }

      // Confirmar ação
      const confirmar = confirm(
        `Tem certeza que deseja desativar ${this.totalInconsistencias} cronograma(s) com inconsistências?\n\n` +
        `Esta ação irá alterar "Jobs Agendados" para FALSE nos cronogramas listados.`
      )

      if (!confirmar) {
        return
      }

      this.desativando = true

      try {
        const cronogramaIds = this.inconsistencias.map(item => item.cronogramaId)
        const response = await hostsService.desativarCronogramasInconsistentes(cronogramaIds)

        if (response && response.success) {
          this.$emit('show-snackbar', `${response.desativados || cronogramaIds.length} cronograma(s) desativado(s) com sucesso!`, 'success')
          
          // Recarregar análise
          await this.analisar()
          
          // Notificar componente pai para atualizar lista
          this.$emit('cronogramas-desativados')
          
          // Emitir evento de análise concluída para atualizar notificação
          this.$emit('analise-concluida', {
            totalInconsistencias: this.totalInconsistencias,
            totalCronogramas: this.totalCronogramas
          })
        } else {
          this.$emit('show-snackbar', response?.error || 'Erro ao desativar cronogramas', 'error')
        }
      } catch (error) {
        console.error('Erro ao desativar cronogramas:', error)
        this.$emit('show-snackbar', error.response?.data?.error || error.message || 'Erro ao desativar cronogramas', 'error')
      } finally {
        this.desativando = false
      }
    },

    getMotivoBadgeClass(motivo) {
      if (motivo.includes('Backup')) {
        return 'badge-warning'
      } else if (motivo.includes('Porta')) {
        return 'badge-danger'
      }
      return 'badge-info'
    },

    getMotivoIcon(motivo) {
      if (motivo.includes('Backup')) {
        return 'mdi-clock-alert'
      } else if (motivo.includes('Porta')) {
        return 'mdi-lock'
      }
      return 'mdi-alert'
    },

    getTooltipText(item, motivo) {
      if (motivo === 'Horários em conflito com Backup' && item.detalhesConflitos && item.detalhesConflitos[motivo]) {
        return true; // Indica que há tooltip
      }
      return null;
    },

    getTooltipLines(item, motivo) {
      if (motivo === 'Horários em conflito com Backup' && item.detalhesConflitos && item.detalhesConflitos[motivo]) {
        const detalhes = item.detalhesConflitos[motivo];
        const linhas = [
          `Horário Fim de Backup: ${detalhes.horarioFimBackup} > Horário Mínimo: ${detalhes.horarioMinimo}`,
          ''
        ];
        detalhes.sistemas.forEach(s => {
          linhas.push(`${s.sistema}: ${s.horario}`);
        });
        return linhas;
      }
      return [];
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    fechar() {
      this.dialog = false
    },

    async ajustarHorarios() {
      // Filtrar apenas inconsistências com conflito de backup
      const conflitosBackup = this.inconsistencias.filter(inc => 
        inc.motivos && inc.motivos.includes('Horários em conflito com Backup')
      )

      if (conflitosBackup.length === 0) {
        this.$emit('show-snackbar', 'Nenhum conflito de backup encontrado para ajustar', 'warning')
        return
      }

      // Confirmar ação
      const confirmar = confirm(
        `Tem certeza que deseja ajustar os horários de ${conflitosBackup.length} host(s) com conflitos de backup?\n\n` +
        `Esta ação irá atualizar automaticamente os horários em conflito para o horário com menos sistemas sendo atualizados.`
      )

      if (!confirmar) {
        return
      }

      this.ajustando = true

      try {
        const response = await hostsService.ajustarHorariosConflito(conflitosBackup)

        if (response && response.success) {
          // Log no console
          console.log('Ajuste de horários:')
          if (response.logs && response.logs.length > 0) {
            response.logs.forEach(log => console.log(log))
          }

          this.$emit('show-snackbar', `${response.ajustes?.length || 0} horário(s) ajustado(s) com sucesso!`, 'success')
          
          // Recarregar análise
          await this.analisar()
          
          // Notificar componente pai para atualizar lista
          this.$emit('cronogramas-desativados')
          
          // Emitir evento de análise concluída para atualizar notificação
          this.$emit('analise-concluida', {
            totalInconsistencias: this.totalInconsistencias,
            totalCronogramas: this.totalCronogramas
          })
        } else {
          this.$emit('show-snackbar', response?.error || 'Erro ao ajustar horários', 'error')
        }
      } catch (error) {
        console.error('Erro ao ajustar horários:', error)
        this.$emit('show-snackbar', error.response?.data?.error || error.message || 'Erro ao ajustar horários', 'error')
      } finally {
        this.ajustando = false
      }
    },

    resetar() {
      this.carregando = false
      this.erro = null
      this.totalCronogramas = 0
      this.totalInconsistencias = 0
      this.inconsistencias = []
      this.desativando = false
      this.ajustando = false
      this.currentPage = 1
    }
  }
}
</script>

<style scoped>
/* Card Principal */
.analise-ambiente-card {
  border-radius: 12px !important;
  overflow: hidden;
}

/* Header */
.analise-header {
  padding: 20px 24px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.theme--dark .analise-header {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.analise-title {
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

.close-btn {
  color: rgba(0, 0, 0, 0.54);
}

.theme--dark .close-btn {
  color: rgba(255, 255, 255, 0.7);
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .close-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

/* Content */
.analise-content {
  background-color: transparent;
}

.analise-loading-text,
.analise-error-text {
  color: rgba(0, 0, 0, 0.87);
}

.theme--dark .analise-loading-text,
.theme--dark .analise-error-text {
  color: rgba(255, 255, 255, 0.87);
}

/* Summary Box */
.summary-box {
  margin: 24px;
  padding: 16px;
  border: 1px solid #FFB020;
  background-color: rgba(255, 176, 32, 0.15);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.theme--light .summary-box {
  background-color: rgba(255, 176, 32, 0.1);
}

.theme--dark .summary-box {
  background-color: rgba(255, 176, 32, 0.2);
}

.summary-box.success {
  border-color: #4CAF50;
  background-color: rgba(76, 175, 80, 0.2);
}

.theme--light .summary-box.success {
  background-color: rgba(76, 175, 80, 0.15);
  border-color: #2E7D32;
}

.theme--dark .summary-box.success {
  background-color: rgba(76, 175, 80, 0.25);
  border-color: #66BB6A;
}

.summary-content h3 {
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
  margin: 0 0 4px 0;
}

.theme--dark .summary-content h3 {
  color: #ffffff;
}

.summary-box.success .summary-content h3 {
  color: #1a1a1a;
  font-weight: 700;
}

.theme--dark .summary-box.success .summary-content h3 {
  color: #ffffff;
  font-weight: 700;
}

.summary-content p {
  color: #1a1a1a;
  font-size: 0.85rem;
  margin: 0;
  font-weight: 500;
}

.theme--dark .summary-content p {
  color: #ffffff;
  font-weight: 500;
}

.summary-box.success .summary-content p {
  color: #1a1a1a;
}

.theme--dark .summary-box.success .summary-content p {
  color: #ffffff;
}

/* List Container */
.list-container {
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
  padding: 0 24px 24px 24px;
}

/* List Header */
.list-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 10px 16px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 700;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .list-header {
  color: rgba(255, 255, 255, 0.7);
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

/* List Row */
.list-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  transition: background-color 0.2s;
  border-radius: 6px;
  margin-bottom: 2px;
}

.theme--dark .list-row {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.list-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .list-row:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

/* Hostname Column */
.hostname-col {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
}

.theme--dark .hostname-col {
  color: rgba(255, 255, 255, 0.87);
}

.server-icon {
  color: #F44336;
  opacity: 0.8;
}

/* Motivos Column */
.motivos-col {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.badge-warning {
  background-color: rgba(255, 176, 32, 0.15);
  color: #FFB020;
  border: 1px solid rgba(255, 176, 32, 0.2);
}

.theme--light .badge-warning {
  background-color: rgba(255, 176, 32, 0.1);
}

.badge-danger {
  background-color: rgba(255, 77, 77, 0.15);
  color: #FF4D4D;
  border: 1px solid rgba(255, 77, 77, 0.2);
}

.theme--light .badge-danger {
  background-color: rgba(255, 77, 77, 0.1);
}

.badge-info {
  background-color: rgba(33, 150, 243, 0.15);
  color: #2196F3;
  border: 1px solid rgba(33, 150, 243, 0.2);
}

.theme--light .badge-info {
  background-color: rgba(33, 150, 243, 0.1);
}

/* Footer */
.analise-footer {
  padding: 16px 24px !important;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.theme--dark .analise-footer {
  border-top-color: rgba(255, 255, 255, 0.12);
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  flex-wrap: wrap;
}

.theme--dark .pagination-info {
  color: rgba(255, 255, 255, 0.7);
}

.pagination-controls {
  display: flex;
  gap: 4px;
}

.footer-actions {
  display: flex;
  gap: 12px;
}

.btn-ghost {
  background: transparent !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.theme--dark .btn-ghost {
  color: rgba(255, 255, 255, 0.87) !important;
}

.btn-ghost:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.theme--dark .btn-ghost:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.btn-primary {
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3) !important;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(244, 67, 54, 0.4) !important;
}

.tooltip-content {
  white-space: pre-line;
  line-height: 1.5;
  font-size: 0.875rem;
}
</style>

